import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, formatApiError } from "../lib/api";
import { Printer, ArrowLeft, Scissors } from "@phosphor-icons/react";

const SITE = (typeof window !== "undefined" ? window.location.origin : "https://curbcrew.co.za");

export default function PrintFlyers() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/cardholder/cards");
        const found = (data.cards || []).find((c) => c.code === code);
        if (!alive) return;
        if (!found) { setErr("Card not found — you can only print flyers for cards you own."); return; }
        setCard(found);
      } catch (e) {
        if (!alive) return;
        // Not signed in → bounce to login. Anything else → show the error.
        if (e?.response?.status === 401) navigate("/cardholder/login");
        else setErr(formatApiError(e));
      }
    })();
    return () => { alive = false; };
  }, [code, navigate]);

  const profileUrl = useMemo(() => card ? `${SITE}/c/${card.code}` : "", [card]);
  // 6 mini cards per A4 page — 2 cols × 3 rows. Bigger, easier to read & hand out.
  const slots = Array.from({ length: 6 }, (_, i) => i);

  if (err) return (
    <div className="p-8 max-w-md mx-auto">
      <Link to="/my-cards" className="label-tag inline-flex items-center gap-2"><ArrowLeft size={14} weight="bold" /> Back</Link>
      <div className="mt-4 bg-red-50 border border-red-300 p-4 label-tag text-red-700" data-testid="print-flyers-error">{err}</div>
    </div>
  );
  if (!card) return <div className="p-8 text-center label-tag">Loading…</div>;

  const skills = (card.skills || []).slice(0, 2).join(" · ");

  return (
    <div className="bg-neutral-200 min-h-screen print:bg-white">
      {/* Screen-only toolbar */}
      <div className="print:hidden border-b border-black bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link to="/my-cards" className="label-tag inline-flex items-center gap-2" data-testid="print-back">
            <ArrowLeft size={14} weight="bold" /> Back to my cards
          </Link>
          <div className="label-tag text-neutral-600">6 mini flyers · 1 A4 page · cut along dotted lines</div>
          <button onClick={() => window.print()} className="label-tag px-4 py-2 bg-[color:var(--cc-yellow)] border border-black brutal-btn inline-flex items-center gap-2" data-testid="print-trigger">
            <Printer size={16} weight="bold" /> Print this page
          </button>
        </div>
        <div className="max-w-4xl mx-auto px-4 pb-3 text-xs text-neutral-700">
          <strong>Tip:</strong> in the print dialog, set <em>Margins → None</em> and <em>Scale → 100%</em>. Print on plain white A4. Then cut along the dotted lines.
        </div>
      </div>

      {/* A4 sheet — 210 × 297 mm */}
      <div className="mx-auto bg-white shadow-lg print:shadow-none print:mx-0 my-6 print:my-0 a4-sheet overflow-hidden" style={{ width: "210mm", minHeight: "297mm", padding: "8mm", boxSizing: "border-box" }}>
        <div
          className="grid w-full h-full"
          style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gridTemplateRows: "repeat(3, minmax(0, 1fr))", gap: 0 }}
        >
          {slots.map((i) => (
            <div
              key={i}
              className="flyer-cell relative"
              style={{ border: "1px dashed #999", padding: "6mm", minHeight: "90mm" }}
            >
              {/* Cut marks at the corners */}
              <Scissors size={12} weight="bold" className="absolute -top-1 -left-1 text-neutral-400 print:text-black" style={{ pointerEvents: "none" }} />

              <div className="flex flex-col h-full justify-between">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[9pt] font-bold tracking-widest uppercase">Curb Crew</div>
                    <div className="text-[8pt] font-mono mt-1">#{card.code}</div>
                  </div>
                  <div className="bg-white border border-black p-1 flex-shrink-0">
                    <QRCodeSVG
                      value={profileUrl}
                      size={150}
                      level="M"
                      bgColor="#ffffff"
                      fgColor="#000000"
                      includeMargin={false}
                    />
                  </div>
                </div>

                <div className="mt-2">
                  <div className="text-[14pt] font-black leading-tight" title={card.full_name}>{card.full_name}</div>
                  {skills && <div className="text-[9pt] uppercase tracking-wide mt-1">{skills}</div>}
                  {card.location && <div className="text-[8pt] text-neutral-700 mt-0.5">{card.location}</div>}
                </div>

                <div className="mt-2 border-t border-dashed border-neutral-400 pt-2">
                  <div className="text-[8pt] leading-tight">
                    Scan with your phone camera — no app needed
                  </div>
                  <div className="text-[7pt] text-neutral-700 mt-1 font-mono">{profileUrl.replace(/^https?:\/\//, "")}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Print-only CSS */}
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          body { background: white !important; }
          .a4-sheet { box-shadow: none !important; margin: 0 !important; }
        }
      `}</style>
    </div>
  );
}
